import { useState, useMemo, useCallback } from 'react';
import { startOfDayIso, endOfDayIso } from '@/lib/datetime/format.js';
import { createFileRoute, Link } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/account/PageHeader.js';
import { BetRow } from '@/components/account/BetRow.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Dices } from 'lucide-react';
import { Money } from '@/components/money/Money.js';
import { DateRangePicker, type DateRange } from '@/components/ui/date-range-picker.js';
import { fetchBets, BETS_QUERY_KEYS, type Bet } from '@/features/bets/api.js';
import { fetchProviders, GAMES_QUERY_KEYS } from '@/features/games/api.js';
import { ORIGINALS } from '@/features/originals/catalog.js';
import { formatDateTime } from '../lib/datetime/format.js';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';

// Originals game code → display name (e.g. 'wheel' → 'Fortune Wheel'). Bet rows
// carry the game code in `gameId` for in-house games.
const ORIGINALS_NAME = new Map(ORIGINALS.map((o) => [o.code, o.name]));

export const Route = createFileRoute('/_player/history')({
  beforeLoad: requireAuth,
  component: HistoryScreen,
});

type OutcomeFilter = 'all' | 'win' | 'loss' | 'push';

function betOutcome(bet: Bet): 'win' | 'loss' | 'push' {
  if (bet.status === 'void' || bet.status === 'cancelled' || bet.status === 'payout_cancelled') {
    return 'push';
  }
  if (bet.status === 'placed') return 'push'; // still open
  const payout = bet.payout ?? 0;
  if (payout > bet.stake) return 'win';
  if (payout === bet.stake) return 'push';
  return 'loss';
}

function HistoryScreen() {
  const { t, i18n } = useTranslation('history');
  const [search, setSearch] = useState('');
  const [outcome, setOutcome] = useState<OutcomeFilter>('all');
  const [providerId, setProviderId] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  const currency = useMoneyCurrency();

  // Provider catalog — powers both the filter (all providers, not just ones the
  // member has wagered on) and name resolution for each row's provider column.
  const providersQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.providers,
    queryFn: fetchProviders,
    staleTime: 60_000,
  });
  const providersById = useMemo(
    () => new Map((providersQuery.data?.items ?? []).map((p) => [p.id, p.displayName])),
    [providersQuery.data],
  );

  // Resolve a real game name for a bet row. `/bets` returns no gameName, so we
  // map the in-house code (gameId, e.g. 'wheel') via the Originals catalog and
  // fall back to the provider display name — never the raw 'other' gameType.
  // ponytail: no game-name resolution for aggregator bets (none exist yet); add
  // a backend /bets enrichment before fetching the full 454-game catalog client-side.
  const gameLabel = useCallback(
    (b: Bet): string =>
      ORIGINALS_NAME.get(b.gameId ?? '') ??
      b.gameName ??
      providersById.get(b.providerId) ??
      b.gameType,
    [providersById],
  );

  // The date range goes to the SERVER. `fetchBets` has accepted `from`/`to`
  // all along and the page never sent them, so a player picking "last month"
  // filtered the 20 rows that happened to be loaded — which for anyone with
  // history is almost always an empty result for a period they certainly played
  // in (WEB-23).
  const fromIso = dateRange?.from ? startOfDayIso(dateRange.from) : undefined;
  const toIso = dateRange?.to ? endOfDayIso(dateRange.to) : undefined;

  const betsQuery = useInfiniteQuery({
    queryKey: BETS_QUERY_KEYS.infinite({
      providerId: providerId !== 'all' ? providerId : undefined,
      from: fromIso,
      to: toIso,
    }),
    queryFn: ({ pageParam = 1 }) =>
      fetchBets({
        page: pageParam as number,
        pageSize: 20,
        providerId: providerId !== 'all' ? providerId : undefined,
        ...(fromIso ? { from: fromIso } : {}),
        ...(toIso ? { to: toIso } : {}),
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.page - 1) * lastPage.pageSize + lastPage.items.length;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 30_000,
  });

  const allBets = useMemo(
    () => betsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [betsQuery.data],
  );

  const providerOptions = useMemo(() => {
    const items = providersQuery.data?.items ?? [];
    return [{ id: 'all', name: '' }, ...items.map((p) => ({ id: p.id, name: p.displayName }))];
  }, [providersQuery.data]);

  const filtered = useMemo(() => {
    return allBets.filter((b) => {
      const o = betOutcome(b);
      if (outcome !== 'all' && o !== outcome) return false;
      if (providerId !== 'all' && b.providerId !== providerId) return false;
      if (search && !gameLabel(b).toLowerCase().includes(search.toLowerCase())) return false;
      // The date range is applied by the server now — see the query above.
      return true;
    });
  }, [allBets, outcome, providerId, search, gameLabel]);

  const totalBets = filtered.length;
  const totalStake = filtered.reduce((s, b) => s + BigInt(b.stake), 0n);
  const totalPayout = filtered.reduce((s, b) => s + BigInt(b.payout ?? 0), 0n);
  const netPL = totalPayout - totalStake;

  // These four are sums over the rows LOADED, not over the account, and they
  // change when the player presses "Load more". They were labelled
  // "Total stake" / "Total payout" / "Net P/L" with nothing qualifying them, so
  // a player with 5,000 lifetime bets was shown a figure computed from their
  // last 20 rounds and told it was their net profit (WEB-23). Saying so is the
  // honest fix available here; a true lifetime figure needs a server-side
  // aggregate that does not exist yet, and inventing one from a page of rows is
  // what created the problem.
  const loadedTotal = betsQuery.data?.pages[0]?.total ?? totalBets;
  const isPartial = totalBets < loadedTotal;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      />

      {/* Filters */}
      <Card className="p-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder={t('filter.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            containerClassName="flex-1"
          />
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            label={t('filter.dateRange')}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {(['all', 'win', 'loss', 'push'] as OutcomeFilter[]).map((o) => (
            <button
              key={o}
              onClick={() => setOutcome(o)}
              className={[
                'h-9 px-3 rounded-lg border text-[13px] font-semibold capitalize transition-colors',
                o === outcome
                  ? 'bg-[var(--primary)] text-[var(--primary-on)] border-[var(--primary)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]',
              ].join(' ')}
            >
              {t(`filter.${o}`)}
            </button>
          ))}
          <select
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
            className="h-9 px-3 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] text-[13px] font-semibold text-[var(--text-primary)] outline-none"
          >
            {providerOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.id === 'all' ? t('filter.allProviders') : p.name}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Summary */}
      <div className="flex flex-col gap-2">
        {isPartial && (
          <div className="text-[11px] text-[var(--text-muted)]">
            {t('stats.scopeNote', { shown: totalBets, total: loadedTotal })}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t('stats.totalBets'),   value: String(totalBets),  money: false, tone: 'var(--text-primary)' },
          { label: t('stats.totalStake'),  value: totalStake,         money: true,  tone: 'var(--text-primary)' },
          { label: t('stats.totalPayout'), value: totalPayout,        money: true,  tone: 'var(--text-primary)' },
          { label: t('stats.netPL'),       value: netPL,              money: true,  tone: netPL >= 0n ? 'var(--success)' : 'var(--danger)' },
        ].map((s) => (
          <Card key={s.label} className="p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{s.label}</div>
            <div className="text-xl font-bold bf-tnum mt-1" style={{ color: s.tone }}>
              {s.money ? (
                <>
                  {(s.value as bigint) < 0n ? '−' : (s.label === 'Net P/L' && (s.value as bigint) >= 0n ? '+' : '')}
                  <Money
                    minor={(s.value as bigint) < 0n ? -(s.value as bigint) : (s.value as bigint)}
                    currency={currency}
                  />
                </>
              ) : (
                s.value
              )}
            </div>
          </Card>
        ))}
        </div>
      </div>

      {betsQuery.isPending && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
          ))}
        </div>
      )}

      {/* Bet list */}
      {!betsQuery.isPending && filtered.length === 0 ? (
        allBets.length === 0 ? (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] py-14 px-6 text-center flex flex-col items-center gap-3">
            <Dices className="size-10 text-[var(--text-muted)]" aria-hidden />
            <div className="text-[16px] font-extrabold text-[var(--text-primary)]">{t('empty.noBetsTitle')}</div>
            <div className="text-[13px] text-[var(--text-secondary)] max-w-sm">
              {t('empty.noBetsBody')}
            </div>
            <Link to="/" className="mt-2">
              <Button glow>{t('empty.playFirstGame')}</Button>
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] py-12 px-6 text-center flex flex-col items-center gap-3">
            <div className="text-[15px] font-bold text-[var(--text-primary)]">{t('empty.title')}</div>
            <div className="text-[13px] text-[var(--text-secondary)]">
              {t('empty.body')}
            </div>
            <Link to="/casino">
              <Button>{t('empty.goToCasino')}</Button>
            </Link>
          </div>
        )
      ) : !betsQuery.isPending ? (
        <>
          <Card className="overflow-hidden">
            {filtered.map((bet, i) => (
              <BetRow
                key={bet.id}
                bet={{
                  id: bet.id,
                  gameName: gameLabel(bet),
                  providerCode: providersById.get(bet.providerId) ?? bet.providerDisplayName ?? bet.providerId,
                  gameType: bet.gameType as never,
                  betAmount: BigInt(bet.stake),
                  payoutAmount: BigInt(bet.payout ?? 0),
                  outcome: betOutcome(bet),
                  time: formatDateTime(bet.createdAt, i18n.language),
                }}
                currency={currency}
                hasBorder={i > 0}
              />
            ))}
          </Card>

          {betsQuery.hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="secondary"
                disabled={betsQuery.isFetchingNextPage}
                onClick={() => betsQuery.fetchNextPage()}
              >
                {betsQuery.isFetchingNextPage ? t('pagination.loading') : t('pagination.loadMore')}
              </Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
